import { FeatureBase } from '../FeatureBase'

// 扩展功能注册表
declare module '../FeatureMap' {
  export interface DeviceFeatureMap {
    mouseCalibration: IMouseCalibrationFeature
  }
}

/** 校准保存类型（保存并退出校准模式时的 SAVE_TYPE 参数） */
export type CalibrationSaveType = 0 | 1 // 0: 厂家校准类型; 1: 用户校准类型

/**
 * 解析后的 CalibrationStatus.flag 状态位
 * （《VT系列LM20 RT磁轴鼠标端USB通信协议 v1.07》Flag 字段 bit0~bit4）：
 *
 *   bit0  非校准模式    | 校准模式
 *   bit1  左键校准中    | 左键校准已通过
 *   bit2  右键校准中    | 右键校准已通过
 *   bit3  左键未触发    | 左键已触发
 *   bit4  右键未触发    | 右键已触发
 *   bit5~7 保留
 *
 * SDK 层在 readCalibrationStatus 内做一次位解析，消费方直接读 boolean，
 * 避免每个上层各自做位移/掩码。
 */
export interface CalibrationFlagBits {
  /** 是否处于校准模式（bit0） */
  inCalibrationMode: boolean
  /** 左键校准是否通过（bit1） */
  leftPassed: boolean
  /** 右键校准是否通过（bit2） */
  rightPassed: boolean
  /** 左键是否已触发（bit3） */
  leftTriggered: boolean
  /** 右键是否已触发（bit4） */
  rightTriggered: boolean
}

/** 单个按键通道的实时校准数据（来自读取校准状态 0xB4） */
export interface CalibrationChannelStatus {
  /** 实时的 ADC 值 (uint16) */
  adc: number
  /** 实时的 LTA (uint16) */
  lta: number
  /** 实时的 Delta (int16) */
  delta: number
  /** 实时的 NorDelta (int16) */
  ndelta: number
  /** 本次校准采集到的最大 Delta (uint16) */
  maxDelta: number
  /** 实时行程级别 (uint8) */
  level: number
}

/** 读取校准状态（0xB4）返回结果 */
export interface CalibrationStatus {
  /**
   * 校准状态标志位（已由 SDK 按协议位定义解析）
   * 来源字节 (uint8)：
   *   bit0: 0=非校准模式, 1=校准模式
   *   bit1: 0=左键校准中, 1=左键校准已通过
   *   bit2: 0=右键校准中, 1=右键校准已通过
   *   bit3: 0=左键未触发, 1=左键已触发
   *   bit4: 0=右键未触发, 1=右键已触发
   *   bit5~7: 保留
   */
  flag: CalibrationFlagBits
  left: CalibrationChannelStatus
  right: CalibrationChannelStatus
}

/** 读取当前行程状态（0xB5）返回结果 */
export interface TriggerStatus {
  /** 左键当前的触发级别，0~10 级 */
  leftTriggerLevel: number
  /** 左键触发状态：bit0=触发行程状态; bit1=RT触发状态 */
  leftTriggerStatus: number
  /** 右键当前的触发级别，0~10 级 */
  rightTriggerLevel: number
  /** 右键触发状态：bit2=触发行程状态; bit3=RT触发状态 */
  rightTriggerStatus: number
}

/** 磁轴按键侧 */
export type RtSide = 'left' | 'right'

/** 一项 RT 参数在左右键上的完整值。 */
export type RtSideValues<T> = Record<RtSide, T>

/** 振动反馈所模拟的微动类型（0=机械；1=光；2=电磁） */
export type RtVibrationFeedbackType = 0 | 1 | 2

/** 设备默认的振动反馈类型：光微动 */
export const DEFAULT_RT_VIBRATION_FEEDBACK_TYPE: RtVibrationFeedbackType = 1

/** 磁轴触发级别配置（按下 + RT 级别，2 字节共享一个 EEPROM 地址） */
export interface RtTriggerConfig {
  /** 按下触发级别 1~10 */
  press: number
  /** RT 触发级别 1~5 */
  rt: number
}

/** 磁轴马达振动配置（2 字节共享一个 EEPROM 地址） */
export interface RtVibration {
  /** 按下振动级别 0~10（0 表示关闭） */
  press: number
  /** 释放振动级别 0~10（0 表示关闭） */
  release: number
}

/** 磁轴 Beep 声音配置（3 字节共享一个 EEPROM 地址） */
export interface RtBeep {
  /** 按下 Beep 类型级别 0~10（0 表示关闭） */
  press: number
  /** 释放 Beep 类型级别 0~10（0 表示关闭） */
  release: number
  /** 音量 1~100（0 表示关闭） */
  volume: number
}

/** 可独立保存、恢复的完整磁轴 RT 配置。 */
export interface MouseRtConfigSnapshot {
  travel: {
    triggerPress: RtSideValues<number>
  }
  rapidTrigger: {
    rtSwitch: RtSideValues<boolean>
    rtLevel: RtSideValues<number>
  }
  vibration: {
    feedbackType: RtVibrationFeedbackType
    vibrationPress: RtSideValues<number>
    vibrationRelease: RtSideValues<number>
  }
  sound: {
    beepPress: RtSideValues<number>
    beepRelease: RtSideValues<number>
    beepVolume: RtSideValues<number>
  }
}

/**
 * 磁轴 (RT / Rapid Trigger) 鼠标校准能力。
 * 对应《VT系列LM20 RT磁轴鼠标端USB通信协议 v1.07》命令码 0xB0~0xB5。
 *
 * 字段粒度的 get/set 全部接受 configIndex 参数，调用方按当前档位传入；
 * 组合配置中的每个字段按其实际 EEPROM 字节地址独立读写。
 */
export interface IMouseCalibrationFeature {
  /** 进入校准模式 (0xB0) */
  enterCalibration(): Promise<void>
  /** 不保存退出校准模式 (0xB1) */
  exitCalibrationWithoutSave(): Promise<void>
  /** 保存并退出校准模式 (0xB2) */
  exitCalibrationAndSave(saveType?: CalibrationSaveType): Promise<void>
  /** 重新开始校准 (0xB3) */
  restartCalibration(): Promise<void>
  /** 读取校准状态 (0xB4) */
  readCalibrationStatus(): Promise<CalibrationStatus>
  /** 读取当前行程状态 (0xB5) */
  readTriggerStatus(): Promise<TriggerStatus>

  /** RT 快速触发开关（false=关; true=开） */
  getRtSwitch(side: RtSide, configIndex?: number): Promise<boolean>
  /** 写入 RT 快速触发开关 */
  setRtSwitch(side: RtSide, value: boolean, configIndex?: number): Promise<void>

  /** 读取按下触发级别（RtTriggerConfig.press） */
  getTriggerPress(side: RtSide, configIndex?: number): Promise<number>
  /** 写入按下触发级别，保留 RT 级别不变 */
  setTriggerPress(side: RtSide, value: number, configIndex?: number): Promise<void>

  /** 读取 RT 触发级别（RtTriggerConfig.rt） */
  getRtLevel(side: RtSide, configIndex?: number): Promise<number>
  /** 写入 RT 触发级别，保留按下触发级别不变 */
  setRtLevel(side: RtSide, value: number, configIndex?: number): Promise<void>

  /** 读取马达振动按下级别（RtVibration.press） */
  getVibrationPress(side: RtSide, configIndex?: number): Promise<number>
  /** 写入马达振动按下级别，保留释放级别不变 */
  setVibrationPress(side: RtSide, value: number, configIndex?: number): Promise<void>

  /** 读取马达振动释放级别（RtVibration.release） */
  getVibrationRelease(side: RtSide, configIndex?: number): Promise<number>
  /** 写入马达振动释放级别，保留按下级别不变 */
  setVibrationRelease(side: RtSide, value: number, configIndex?: number): Promise<void>

  /** 读取振动反馈类型（0=机械微动；1=光微动；2=电磁微动） */
  getVibrationFeedbackType(configIndex?: number): Promise<RtVibrationFeedbackType>
  /** 写入振动反馈类型 */
  setVibrationFeedbackType(value: RtVibrationFeedbackType, configIndex?: number): Promise<void>

  /** 读取 Beep 按下级别（RtBeep.press） */
  getBeepPress(side: RtSide, configIndex?: number): Promise<number>
  /** 写入 Beep 按下级别，保留释放级别与音量不变 */
  setBeepPress(side: RtSide, value: number, configIndex?: number): Promise<void>

  /** 读取 Beep 释放级别（RtBeep.release） */
  getBeepRelease(side: RtSide, configIndex?: number): Promise<number>
  /** 写入 Beep 释放级别，保留按下级别与音量不变 */
  setBeepRelease(side: RtSide, value: number, configIndex?: number): Promise<void>

  /** 读取 Beep 音量（RtBeep.volume） */
  getBeepVolume(side: RtSide, configIndex?: number): Promise<number>
  /** 写入 Beep 音量，保留按下/释放级别不变 */
  setBeepVolume(side: RtSide, value: number, configIndex?: number): Promise<void>
}

const RT_SIDES: RtSide[] = ['left', 'right']

/**
 * 通过字段级 getter 从设备读取完整 RT 配置。
 * 全局设备快照和单独 RT 预设共用此入口，避免上层从页面缓存拼装数据。
 */
export async function readMouseRtConfigSnapshot(
  feature: IMouseCalibrationFeature,
  configIndex?: number,
): Promise<MouseRtConfigSnapshot> {
  const [feedbackType, sideResults] = await Promise.all([
    feature.getVibrationFeedbackType(configIndex),
    Promise.all(
      RT_SIDES.map(async (side) => {
        const [
          rtSwitch,
          triggerPress,
          rtLevel,
          vibrationPress,
          vibrationRelease,
          beepPress,
          beepRelease,
          beepVolume,
        ] = await Promise.all([
          feature.getRtSwitch(side, configIndex),
          feature.getTriggerPress(side, configIndex),
          feature.getRtLevel(side, configIndex),
          feature.getVibrationPress(side, configIndex),
          feature.getVibrationRelease(side, configIndex),
          feature.getBeepPress(side, configIndex),
          feature.getBeepRelease(side, configIndex),
          feature.getBeepVolume(side, configIndex),
        ])
        return {
          side,
          rtSwitch,
          triggerPress,
          rtLevel,
          vibrationPress,
          vibrationRelease,
          beepPress,
          beepRelease,
          beepVolume,
        }
      }),
    ),
  ])

  const bySide = Object.fromEntries(sideResults.map((result) => [result.side, result])) as Record<
    RtSide,
    (typeof sideResults)[number]
  >
  const values = <K extends keyof (typeof sideResults)[number]>(key: K) => ({
    left: bySide.left[key],
    right: bySide.right[key],
  })

  return {
    travel: { triggerPress: values('triggerPress') as RtSideValues<number> },
    rapidTrigger: {
      rtSwitch: values('rtSwitch') as RtSideValues<boolean>,
      rtLevel: values('rtLevel') as RtSideValues<number>,
    },
    vibration: {
      feedbackType,
      vibrationPress: values('vibrationPress') as RtSideValues<number>,
      vibrationRelease: values('vibrationRelease') as RtSideValues<number>,
    },
    sound: {
      beepPress: values('beepPress') as RtSideValues<number>,
      beepRelease: values('beepRelease') as RtSideValues<number>,
      beepVolume: values('beepVolume') as RtSideValues<number>,
    },
  }
}

/** 将完整 RT 配置写入指定设备配置槽；最后写开关，避免参数未完成时提前启用 RT。 */
export async function writeMouseRtConfigSnapshot(
  feature: IMouseCalibrationFeature,
  snapshot: MouseRtConfigSnapshot,
  configIndex?: number,
): Promise<void> {
  await Promise.all([
    feature.setVibrationFeedbackType(snapshot.vibration.feedbackType, configIndex),
    ...RT_SIDES.flatMap((side) => [
      feature.setTriggerPress(side, snapshot.travel.triggerPress[side], configIndex),
      feature.setRtLevel(side, snapshot.rapidTrigger.rtLevel[side], configIndex),
      feature.setVibrationPress(side, snapshot.vibration.vibrationPress[side], configIndex),
      feature.setVibrationRelease(side, snapshot.vibration.vibrationRelease[side], configIndex),
      feature.setBeepPress(side, snapshot.sound.beepPress[side], configIndex),
      feature.setBeepRelease(side, snapshot.sound.beepRelease[side], configIndex),
      feature.setBeepVolume(side, snapshot.sound.beepVolume[side], configIndex),
    ]),
  ])
  await Promise.all(
    RT_SIDES.map((side) =>
      feature.setRtSwitch(side, snapshot.rapidTrigger.rtSwitch[side], configIndex),
    ),
  )
}

export class MouseCalibrationFeature extends FeatureBase implements IMouseCalibrationFeature {
  enterCalibration(): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.enterCalibration')
  }
  exitCalibrationWithoutSave(): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.exitCalibrationWithoutSave')
  }
  exitCalibrationAndSave(_saveType?: CalibrationSaveType): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.exitCalibrationAndSave')
  }
  restartCalibration(): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.restartCalibration')
  }
  readCalibrationStatus(): Promise<CalibrationStatus> {
    return this.unsupported('MouseCalibrationFeature.readCalibrationStatus')
  }
  readTriggerStatus(): Promise<TriggerStatus> {
    return this.unsupported('MouseCalibrationFeature.readTriggerStatus')
  }
  getRtSwitch(_side: RtSide, _configIndex?: number): Promise<boolean> {
    return this.unsupported('MouseCalibrationFeature.getRtSwitch')
  }
  setRtSwitch(_side: RtSide, _value: boolean, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setRtSwitch')
  }
  getTriggerPress(_side: RtSide, _configIndex?: number): Promise<number> {
    return this.unsupported('MouseCalibrationFeature.getTriggerPress')
  }
  setTriggerPress(_side: RtSide, _value: number, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setTriggerPress')
  }
  getRtLevel(_side: RtSide, _configIndex?: number): Promise<number> {
    return this.unsupported('MouseCalibrationFeature.getRtLevel')
  }
  setRtLevel(_side: RtSide, _value: number, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setRtLevel')
  }
  getVibrationPress(_side: RtSide, _configIndex?: number): Promise<number> {
    return this.unsupported('MouseCalibrationFeature.getVibrationPress')
  }
  setVibrationPress(_side: RtSide, _value: number, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setVibrationPress')
  }
  getVibrationRelease(_side: RtSide, _configIndex?: number): Promise<number> {
    return this.unsupported('MouseCalibrationFeature.getVibrationRelease')
  }
  setVibrationRelease(_side: RtSide, _value: number, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setVibrationRelease')
  }
  getVibrationFeedbackType(_configIndex?: number): Promise<RtVibrationFeedbackType> {
    return this.unsupported('MouseCalibrationFeature.getVibrationFeedbackType')
  }
  setVibrationFeedbackType(_value: RtVibrationFeedbackType, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setVibrationFeedbackType')
  }
  getBeepPress(_side: RtSide, _configIndex?: number): Promise<number> {
    return this.unsupported('MouseCalibrationFeature.getBeepPress')
  }
  setBeepPress(_side: RtSide, _value: number, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setBeepPress')
  }
  getBeepRelease(_side: RtSide, _configIndex?: number): Promise<number> {
    return this.unsupported('MouseCalibrationFeature.getBeepRelease')
  }
  setBeepRelease(_side: RtSide, _value: number, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setBeepRelease')
  }
  getBeepVolume(_side: RtSide, _configIndex?: number): Promise<number> {
    return this.unsupported('MouseCalibrationFeature.getBeepVolume')
  }
  setBeepVolume(_side: RtSide, _value: number, _configIndex?: number): Promise<void> {
    return this.unsupported('MouseCalibrationFeature.setBeepVolume')
  }
}
